// Bootstrap for the FingerprintCLF collection page. Renders Turnstile when a
// site key is provided, then drives the shared probes via runCollection().
//
// Build-time env vars (Vite):
//   VITE_WORKER_URL          — base URL of the Cloudflare Worker
//   VITE_TURNSTILE_SITE_KEY  — Turnstile site key (empty = skip Turnstile)

import {
  runCollection,
  parseBrowserName,
  parseOS,
  extractBrowserVersion,
  type ServerResponse,
} from '@fingerprintclf/probes';

const WORKER_URL = (import.meta.env['VITE_WORKER_URL'] as string | undefined) || 'http://localhost:8989';
const TURNSTILE_SITE_KEY = (import.meta.env['VITE_TURNSTILE_SITE_KEY'] as string | undefined) || '';

declare global {
  interface Window {
    turnstile?: { render: (el: HTMLElement, opts: Record<string, unknown>) => string };
  }
}

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function showError(msg: string): void {
  const loader = $('loader');
  if (loader) loader.style.display = 'none';
  const el = $('error');
  if (!el) return;
  el.style.display = 'block';
  el.textContent = msg;
}

function progress(step: number, total: number, label: string): void {
  const bar = $('progress');
  const txt = $('loader-text');
  if (bar) bar.style.width = `${Math.round((step / total) * 100)}%`;
  if (txt) txt.textContent = label;
}

function loadTurnstileToken(): Promise<string | undefined> {
  return new Promise((resolve) => {
    if (!TURNSTILE_SITE_KEY) {
      resolve(undefined);
      return;
    }
    const host = $('turnstile-host');
    if (!host) { resolve(undefined); return; }
    const widget = document.createElement('div');
    widget.className = 'cf-turnstile';
    host.appendChild(widget);

    let resolved = false;
    const onSuccess = (token: string) => {
      if (resolved) return;
      resolved = true;
      resolve(token);
    };
    const onError = () => {
      if (resolved) return;
      resolved = true;
      resolve(undefined);
    };

    // Watchdog — Turnstile occasionally hangs in headless / restricted environments.
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        resolve(undefined);
      }
    }, 15000);

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__fpclfTurnstileReady&render=explicit';
    script.async = true;
    script.defer = true;
    // Pass function refs (not string names) — Turnstile's lookup-by-name path
    // crashes ("u.call is not a function") when bundlers minify the globals.
    // The widget mode (invisible) is fixed by the Site Key, so we don't need
    // to pass `size`.
    (window as unknown as { __fpclfTurnstileReady?: () => void }).__fpclfTurnstileReady = () => {
      try {
        window.turnstile?.render(widget, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: onSuccess,
          'error-callback': onError,
          'timeout-callback': onError,
        });
      } catch {
        if (!resolved) {
          resolved = true;
          resolve(undefined);
        }
      }
    };
    document.head.appendChild(script);
  });
}

function renderResults(fp: Record<string, unknown>, server: ServerResponse): void {
  const score = typeof server.score === 'number' && server.score >= 0 ? server.score : 0;
  const nav = (fp['navigator'] as Record<string, unknown>) ?? {};
  const scr = (fp['screen'] as Record<string, unknown>) ?? {};
  const webgl = (fp['webgl'] as Record<string, unknown>) ?? {};
  const ua = (nav['userAgent'] as string) ?? '';

  const loader = $('loader');
  if (loader) loader.style.display = 'none';
  const results = $('results');
  if (results) results.style.display = 'block';

  const arc = $('score-arc') as SVGCircleElement | null;
  if (arc) {
    const circumference = 2 * Math.PI * 54;
    const offset = circumference - (score / 100) * circumference;
    arc.style.strokeDashoffset = String(offset);
    if (score >= 80) arc.style.stroke = 'var(--green)';
    else if (score >= 50) arc.style.stroke = 'var(--yellow)';
    else arc.style.stroke = 'var(--red)';
  }

  const num = $('score-num');
  if (num) num.textContent = String(score);

  const verdict = $('score-verdict');
  if (verdict) {
    if (score >= 80) verdict.textContent = 'Excellent Compatibility';
    else if (score >= 60) verdict.textContent = 'Good Compatibility';
    else if (score >= 40) verdict.textContent = 'Fair Compatibility';
    else verdict.textContent = 'Limited Compatibility';
  }

  const browserName = parseBrowserName(ua);
  const browserVer = (fp['browserVersion'] as string) || extractBrowserVersion(ua);
  const setText = (id: string, v: string) => { const el = $(id); if (el) el.textContent = v; };
  setText('info-browser', browserName + (browserVer ? ` ${browserVer}` : ''));
  setText('info-os', parseOS(ua));
  const w = (scr['width'] as number) || 0;
  const h = (scr['height'] as number) || 0;
  const dpr = (scr['devicePixelRatio'] as number) || 1;
  setText('info-screen', `${w} x ${h} @${dpr}x`);

  const params = (webgl['params'] as Record<string, unknown>) ?? {};
  const gpuName = (webgl['renderer'] as string) || (params['RENDERER'] as string) || 'Not available';
  setText('info-gpu', gpuName);
  const ext1 = ((webgl['extensions'] as unknown[]) ?? []).length;
  const ext2 = ((webgl['extensions2'] as unknown[]) ?? []).length;
  setText('info-glext', `${ext1} (WebGL1) / ${ext2} (WebGL2)`);
  setText('info-audio', (fp['audioHash'] as string) ? 'Yes' : 'No');

  const showParams = [
    'MAX_TEXTURE_SIZE', 'MAX_RENDERBUFFER_SIZE', 'MAX_VIEWPORT_DIMS',
    'MAX_VERTEX_ATTRIBS', 'MAX_VERTEX_UNIFORM_VECTORS',
    'MAX_FRAGMENT_UNIFORM_VECTORS', 'MAX_VARYING_VECTORS',
    'DEPTH_BITS', 'STENCIL_BITS', 'VERSION', 'SHADING_LANGUAGE_VERSION',
  ];
  let webglHtml = '';
  for (const k of showParams) {
    const v = params[k];
    if (v === undefined || v === null) continue;
    const display = Array.isArray(v) ? v.join(', ') : String(v);
    webglHtml += `<div class="detail-row"><span class="dk">${k}</span><span class="dv">${escapeHtml(display)}</span></div>`;
  }
  if (webglHtml) {
    const el = $('webgl-details');
    if (el) el.innerHTML = webglHtml;
    const sec = $('detail-webgl');
    if (sec) sec.style.display = 'block';
  }

  const tz = (fp['timezone'] as Record<string, unknown>) ?? {};
  const pm = (fp['performanceMemory'] as Record<string, unknown>) ?? {};
  const conn = (fp['connection'] as Record<string, unknown>) ?? {};
  const plg = (fp['plugins'] as Record<string, unknown>) ?? {};
  const fonts = (fp['fonts'] as unknown[]) ?? [];
  const voices = (fp['voices'] as unknown[]) ?? [];

  const sysRows: Array<[string, string]> = [];
  sysRows.push(['CPU Cores', String(nav['hardwareConcurrency'] ?? 'N/A')]);
  const mem = nav['deviceMemory'] as number | undefined;
  sysRows.push(['Device Memory', mem ? `${mem} GB` : 'N/A']);
  sysRows.push(['Touch Points', String(nav['maxTouchPoints'] ?? '0')]);
  sysRows.push(['Language', String(nav['language'] ?? 'N/A')]);
  sysRows.push(['Languages', ((nav['languages'] as string[]) ?? []).join(', ') || 'N/A']);
  sysRows.push(['Color Depth', scr['colorDepth'] ? `${scr['colorDepth'] as number} bit` : 'N/A']);
  sysRows.push(['Fonts Detected', String(fonts.length)]);
  sysRows.push(['Voices Detected', String(voices.length)]);
  sysRows.push(['Canvas Hash', String(fp['canvasHash'] ?? 'N/A')]);
  sysRows.push(['Audio Hash', String(fp['audioHash'] ?? 'N/A')]);
  if (tz['name']) {
    const offMin = (tz['offsetMinutes'] as number) ?? 0;
    const tzHours = offMin / 60;
    sysRows.push(['Timezone', `${tz['name'] as string} (UTC${tzHours >= 0 ? '+' : ''}${tzHours})`]);
  }
  if (pm['supported']) {
    const heapGB = ((pm['jsHeapSizeLimit'] as number) / (1024 * 1024 * 1024)).toFixed(2);
    sysRows.push(['JS Heap Limit', `${heapGB} GB`]);
  }
  if (conn['supported']) {
    sysRows.push(['Connection', String(conn['effectiveType'] ?? 'unknown')]);
  }
  sysRows.push(['Plugins', String(plg['count'] ?? 'N/A')]);
  if (plg['pdfViewerEnabled'] !== null && plg['pdfViewerEnabled'] !== undefined) {
    sysRows.push(['PDF Viewer', plg['pdfViewerEnabled'] ? 'Enabled' : 'Disabled']);
  }
  if (server.eligible !== undefined) {
    sysRows.push(['Template Eligible', server.eligible ? 'Yes' : 'No']);
  }
  if (server.promoted !== undefined) {
    sysRows.push(['Auto-promoted', server.promoted ? 'Yes' : 'No']);
  }
  const sourceTag = readSourceTag();
  if (sourceTag) sysRows.push(['Source', sourceTag]);
  let sysHtml = '';
  for (const [k, v] of sysRows) {
    sysHtml += `<div class="detail-row"><span class="dk">${escapeHtml(k)}</span><span class="dv">${escapeHtml(v)}</span></div>`;
  }
  const sysEl = $('system-details');
  if (sysEl) sysEl.innerHTML = sysHtml;
  const sysSec = $('detail-system');
  if (sysSec) sysSec.style.display = 'block';
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function readSourceTag(): string {
  // ?source=foo wins; falls back to ?utm_source=foo for campaign URLs that
  // already use the standard tracker. Trimmed + capped at 64 chars to match
  // the Worker-side data_source column length budget.
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('source') ?? params.get('utm_source') ?? '';
  return raw.trim().slice(0, 64);
}

async function bootstrap(): Promise<void> {
  // Render the loader UI first; kick off Turnstile & probe collection in parallel.
  await new Promise((r) => setTimeout(r, 200));
  const sourceTag = readSourceTag();
  if (sourceTag) {
    const txt = $('loader-text');
    if (txt) txt.textContent = `Initializing checks (source: ${sourceTag})...`;
  }
  try {
    const turnstileToken = await loadTurnstileToken();
    const result = await runCollection({
      workerUrl: WORKER_URL,
      ...(turnstileToken !== undefined ? { turnstileToken } : {}),
      ...(sourceTag ? { source: sourceTag } : {}),
      onProgress: progress,
    });
    if (result.server.detail && result.server.score === 0) {
      // Server explicitly errored; surface the message but still render local fp data.
      renderResults(result.fingerprint, result.server);
      const err = $('error');
      if (err) {
        err.style.display = 'block';
        err.textContent = `submission warning: ${result.server.detail}`;
      }
    } else {
      renderResults(result.fingerprint, result.server);
    }
  } catch (e) {
    showError(`unexpected error: ${(e as Error).message ?? String(e)}`);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => { void bootstrap(); });
} else {
  void bootstrap();
}
